import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nomination, NominationStatus } from '../../types/nomination';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { getStatusBadgeClass, formatNominationDate, cleanReason } from './NominationCard';
import RightSidePanel from '../performance/RightSidePanel';
import CreateNomination from './CreateNomination';
import EmptyState from '../common/EmptyState';
import PageHeader from '../common/PageHeader';
import './NominationList.css';

const NominationList: React.FC = () => {
  const renderStart = performance.now();
  console.log('[Perf][MyNominations] render start');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  const loadNominations = useCallback(async () => {
    if (user?.id) {
      const startedAt = performance.now();
      console.log('[Perf][MyNominations] API start NominationServiceWrapper.getMyNominations', {
        userId: user.id,
        userEmail: user.email,
      });
      const myNominations = await NominationServiceWrapper.getMyNominations(user.id, user.email);
      console.log('[Perf][MyNominations] API complete NominationServiceWrapper.getMyNominations', {
        durationMs: Math.round(performance.now() - startedAt),
        count: myNominations.length,
      });
      setNominations(myNominations);
    }
  }, [user]);

  useEffect(() => { loadNominations(); }, [loadNominations]);

  const filtered = filter === 'all' ? nominations : nominations.filter(n => (n.status || '').toLowerCase() === filter.toLowerCase());

  const statusCounts = {
    all: nominations.length,
    [NominationStatus.DRAFT]: nominations.filter(n => (n.status || '').toLowerCase() === NominationStatus.DRAFT.toLowerCase()).length,
    [NominationStatus.SUBMITTED]: nominations.filter(n => (n.status || '').toLowerCase() === NominationStatus.SUBMITTED.toLowerCase()).length,
    [NominationStatus.APPROVED]: nominations.filter(n => (n.status || '').toLowerCase() === NominationStatus.APPROVED.toLowerCase()).length,
    [NominationStatus.PUBLISHED]: nominations.filter(n => (n.status || '').toLowerCase() === NominationStatus.PUBLISHED.toLowerCase()).length,
    [NominationStatus.REJECTED]: nominations.filter(n => (n.status || '').toLowerCase() === NominationStatus.REJECTED.toLowerCase()).length,
  };

  useEffect(() => {
    console.log('[Perf][MyNominations] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      nominations: nominations.length,
      filtered: filtered.length,
      filter,
    });
  });

  return (
    <div className="nom-list-page">
      <PageHeader
        icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
        title="My Nominations"
        subtitle={`${nominations.length} nomination${nominations.length !== 1 ? 's' : ''} submitted`}
        gradient="linear-gradient(135deg, #0d8a6a 0%, #065f46 50%, #064e3b 100%)"
        accentColor="#0d8a6a"
        actions={
          <button className="nom-list-new-btn" onClick={() => setShowCreatePanel(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Nomination
          </button>
        }
      />

      <div className="nom-list-filters">
        {[['all', 'All'], [NominationStatus.DRAFT, 'Draft'], [NominationStatus.SUBMITTED, 'Submitted'], [NominationStatus.APPROVED, 'Approved'], [NominationStatus.PUBLISHED, 'Published'], [NominationStatus.REJECTED, 'Rejected']].map(([key, label]) => (
          <button key={key} className={`nom-filter-btn ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
            {label} {(statusCounts as any)[key] > 0 && <span className="nom-filter-count">{(statusCounts as any)[key]}</span>}
          </button>
        ))}
      </div>

      <div className="nom-list-items">
        {filtered.length === 0 ? (
          <EmptyState
            icon="star"
            title="No Nominations Found"
            subtitle="Create your first nomination to recognize an outstanding colleague."
            actionLabel="+ New Nomination"
            onAction={() => setShowCreatePanel(true)}
          />
        ) : (
          filtered.map(nom => (
            <div className="nom-list-card" key={nom.id} onClick={() => navigate(`/nominations/${nom.id}`)}>
              <div className="nom-list-card-left">
                {nom.nomineePhotoUrl ? (
                  <img src={nom.nomineePhotoUrl} alt={nom.nomineeName} className="nom-list-photo" />
                ) : (
                  <div className="nom-list-avatar">{nom.nomineeName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                )}
              </div>
              <div className="nom-list-card-body">
                <div className="nom-list-card-top-row">
                  <h3>{nom.nomineeName}</h3>
                  <span className={`nomination-status-badge ${getStatusBadgeClass(nom.status)}`}>{nom.status}</span>
                </div>
                <span className="nom-list-award">{nom.awardType}</span>
                <p className="nom-list-reason">{cleanReason(nom.reason)}</p>
                <div className="nom-list-meta">
                  <span>Created {formatNominationDate(nom.createdAt)}</span>
                  {nom.publishedAt && <span>Published {formatNominationDate(nom.publishedAt)}</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Nomination Side Panel */}
      <RightSidePanel
        isOpen={showCreatePanel}
        title="New Nomination"
        onClose={() => setShowCreatePanel(false)}
        wide
        className="pms-right-side-panel--nomination"
      >
        <CreateNomination onComplete={() => { setShowCreatePanel(false); loadNominations(); }} embedded />
      </RightSidePanel>
    </div>
  );
};

export default NominationList;
